import { Component, HostBinding, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { WidgetAlertsSettingsDialogComponent } from './widget-alerts-settings-dialog.component';
import { AdStatsAlertsService } from '../../../../../shared/services/ad-stats-alerts.service';
import { DashAlert } from '../../../domain/alerts/dash-alert.model';
import { AlertGroupingType, AlertRequest } from '../../../domain/alerts/alert-request.model';
import { AlertMetric } from '../../../domain/alerts/alert-metric.enum';
import { AlertReportType } from '../../../domain/alerts/alert-report-type.enum';
import { DateRangeType } from '../../../../../shared/domain/date-range-type.enum';
import { MomentDateUtils } from '../../../../../shared/utils/moment-date-utils';
import { DateRangeHolder } from '../../../../../shared/domain/date-range-holder.model';
import { WidgetAlertsSettings } from './widget-alerts-settings.model';
import { SnackBarService } from '../../../../../shared/services/snackbar.service';
import { PublisherMultiselectSearchDialogComponent } from '../../publisher-multiselect-search-dialog.component';

@Component({
  selector: 'cm-widget-alerts',
  template: `
    <cm-widget [menuVisible]="menuVisible">
      <cm-widget-title>
        Alerts
        <!--<span class="cm-card-title-action d-none d-md-inline-flex">
          <i class="fas fa-cog" aria-hidden="true" (click)="openSettings()"></i>
        </span>-->
      </cm-widget-title>
      <cm-widget-actions class="align-items-center">
        <cm-publisher-multiselect-search-dropdown
          [selectedPublisherIds]="settings?.request?.filter.publisherIds"
          (selectionIdsChange)="settings?.request?.filter.publisherIds = $event; refreshData()"
          class="d-none d-md-inline-flex">
        </cm-publisher-multiselect-search-dropdown>

        <cm-date-range-dropdown [dateRange]="settings.dateRange"
                                [allowedDateRangeTypes]="allowedDateRangeTypes"
                                (dateRangeChanged)="onDateRangeChanged($event)"
                                class="d-none d-md-inline-flex">
        </cm-date-range-dropdown>
        <button mat-icon-button color="primary" (click)="openSettings()" matTooltip="Alerts settings">
	        <i class="fas fa-cog"></i>
        </button>
      </cm-widget-actions>
      <cm-widget-content>
        <div *ngIf="loading" class="card-message">
          <mat-spinner [color]="'primary'" [mode]="'indeterminate'" [diameter]="'30'"></mat-spinner>
        </div>
        <p *ngIf="noData" [hidden]="loading" class="card-message">No alerts available for chosen settings</p>
        <p *ngIf="!settings.request" class="card-message">
          <i class="fas fa-cog"></i>
          <span>Please choose widget settings</span>
        </p>
        <cm-widget-alerts-table [hidden]="loading || noData"
                                (rowClick)="onRowClick($event)"
                                [paginator]="paginator" #table
                                [data]="data"></cm-widget-alerts-table>
      </cm-widget-content>
      <cm-widget-footer>
        <mat-paginator #paginator
                       [hidden]="loading || noData"
                       [pageSize]="10"
                       [pageSizeOptions]="[5, 10, 20]">
        </mat-paginator>
      </cm-widget-footer>
    </cm-widget>
  `,
  styles: [`
    .mat-row {
      min-height: 60px;
    }
  `],
  host: { 'class': 'cm-widget-alerts' }
})
export class WidgetAlertsComponent implements OnInit {

  @Input()
  loading = false;

  @Input()
  menuVisible = false;

  allowedDateRangeTypes = [
    DateRangeType.TODAY,
    DateRangeType.YESTERDAY,
    DateRangeType.THIS_WEEK,
    DateRangeType.LAST_7_DAYS,
  ];

  data: DashAlert[] = [];

  noData = false;

  settings = <WidgetAlertsSettings>{
    minimized: false,
    expanded: false,
    request: new AlertRequest(),
  };

  private dateRange: DateRangeHolder;

  constructor(private matDialog: MatDialog,
    private sbs: SnackBarService,
    private router: Router,
    private service: AdStatsAlertsService) {
    this.settings.dateRange = MomentDateUtils.getDatesFromRange(DateRangeType.THIS_WEEK);
    this.settings.request = <AlertRequest>{
      reportType: AlertReportType.DAILY,
      groupingType: AlertGroupingType.ALERT,
      metrics: [
        AlertMetric.IMPRESSIONS_DROP_PERCENT,
        AlertMetric.REVENUE_DROP_PERCENT,
        AlertMetric.IMPRESSIONS_STOPPED_DELIVERING,
        AlertMetric.REVENUE_STOPPED_DELIVERING,
      ],
      filter: {
        impressionsDropPercent: 80,
        revenueDropPercent: 50,
        revenueOver: 100,
        impressionsOver: 10000,
        dateFrom: this.settings.dateRange.dateFrom,
        dateTo: this.settings.dateRange.dateTo,
      },
    };
  }

  onRowClick(row: DashAlert) {
    this.service.widgetRequest = this.settings.request;
    this.service.widgetRequest.metrics = [row.alertMetric];
    this.router.navigate(['/troubleshooting/alerts']);
  }

  ngOnInit(): void {
    this.refreshData();
  }

  onDateRangeChanged(date: DateRangeHolder) {
    this.settings.dateRange = date;
    this.settings.request.filter.dateFrom = date.dateFrom;
    this.settings.request.filter.dateTo = date.dateTo;
    this.refreshData();
  }

  refreshData() {
    if (!this.settings.request) {
      return;
    }
    this.loading = true;
    this.data = [];
    this.service.alerts(this.settings.request).subscribe(
      next => {
        this.noData = !next || !next.length;
        this.data = next;
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.sbs.error('Unexpected error occurred');
      });
  }

  openSettings() {
    this.matDialog.open(WidgetAlertsSettingsDialogComponent, <MatDialogConfig>{
      data: {
        settings: this.settings
      },
      width: '800px'
    }).afterClosed().subscribe(result => {
      if (result) {
        this.settings = result;
        this.refreshData();
      }
    });
  }


  openPublisherFilterDialog() {
    this.matDialog.open(PublisherMultiselectSearchDialogComponent, {
      data: {
        selectedPublisherIds: this.settings.request.filter.publisherIds
      },
      width: '400px'
    }).afterClosed().subscribe(next => {
      if (next) {
        this.settings.request.filter.publisherIds = next.map(p => p.id);
        this.refreshData();
      }
    });
  }

}

